export type PinState = 'unpinned' | 'pinned' | 'pinned-shut';

const VALID_PIN_STATES: PinState[] = ['unpinned', 'pinned', 'pinned-shut'];

// Client-only shared refs, so the layout and the sidebar itself see one state.
// Never created on the server: module scope there outlives the request, and a
// shared ref would render the first visitor's sidebar state into every later
// response (and go stale the moment that visitor re-pinned).
let _pinState: Ref<PinState> | undefined;
let _hoverOpen: Ref<boolean> | undefined;
let _transientLock: Ref<boolean> | undefined;

/**
 * Read pin state directly from document.cookie, bypassing useCookie/useState
 * which hydrate from the build-time payload in static builds.
 * Returns null if no cookie found (caller should fallback to useCookieStorage).
 */
function readPinFromCookie(): PinState | null {
	if (typeof document === 'undefined') return null;
	const match = document.cookie.match(/(?:^|; )ls-user-lock-sidebar=([^;]*)/);
	if (!match?.[1]) return null;
	try {
		const val = JSON.parse(decodeURIComponent(match[1] as string));
		return VALID_PIN_STATES.includes(val) ? val : null;
	} catch {
		const val = decodeURIComponent(match[1] as string);
		return VALID_PIN_STATES.includes(val as PinState) ? (val as PinState) : null;
	}
}

export const useSidebar = () => {
	// useCookieStorage for WRITING — persists to document.cookie via useCookie.
	const pinCookie = useCookieStorage<PinState>('ls-user-lock-sidebar', {
		default: () => 'unpinned',
	});

	// Resolved once, up front, so the FIRST render is already the user's state:
	// a width that only settles a tick later plays as an open/close animation.
	// document.cookie wins over the useCookie ref because that ref hydrates from
	// the build-time payload in static builds. Reading it this early cannot
	// mismatch during hydration — the shell renders behind AuthBootGate, so no
	// build has sidebar markup in its prerendered HTML, and SSR renders from the
	// request's own cookie.
	const initialPin =
		(import.meta.client ? readPinFromCookie() : null) ??
		(pinCookie.value as PinState | null) ??
		'unpinned';

	const pinValue = import.meta.client ? (_pinState ??= ref(initialPin)) : ref(initialPin);
	const hoverOpen = import.meta.client ? (_hoverOpen ??= ref(false)) : ref(false);
	const transientLock = import.meta.client ? (_transientLock ??= ref(false)) : ref(false);

	// Writable: reads the live state, writes through to the cookie.
	const pinState = computed({
		get: () => pinValue.value,
		set: (val: PinState) => {
			pinValue.value = val;
			pinCookie.value = val;
		},
	});

	// Derived open state — single source of truth.
	// pinned → always open, pinned-shut → always closed, unpinned → follows hover.
	const isSidebarOpen = computed(() => {
		switch (pinState.value) {
			case 'pinned':
				return true;
			case 'pinned-shut':
				return false;
			case 'unpinned':
			default:
				return hoverOpen.value;
		}
	});

	/**
	 * Toggle sidebar open/closed for hover behavior.
	 * Blocked by transient lock or any non-"unpinned" pin state.
	 */
	function toggleOpen(value?: boolean) {
		if (transientLock.value) return;
		if (pinState.value !== 'unpinned') return;
		hoverOpen.value = value ?? !hoverOpen.value;
	}

	/**
	 * Programmatic transient lock (e.g., account selector is open).
	 * Forces sidebar open while locked.
	 */
	function toggleLock(value?: boolean) {
		transientLock.value = value ?? !transientLock.value;
		if (transientLock.value) {
			hoverOpen.value = true;
		}
	}

	/**
	 * Toggle user pin state. Accepts boolean for legacy callers.
	 */
	function toggleUserLock(value?: boolean) {
		if (value === true) {
			pinState.value = 'pinned';
		} else if (value === false) {
			pinState.value = 'unpinned';
		} else {
			pinState.value = pinState.value === 'unpinned' ? 'pinned' : 'unpinned';
		}
	}

	/** Direct setter for hover-open state. Bypasses lock guards. */
	function setOpen(open: boolean) {
		hoverOpen.value = open;
	}

	return {
		isSidebarOpen: readonly(isSidebarOpen),
		isSidebarLocked: transientLock,
		isUserSidebarLocked: pinState,
		toggleOpen,
		toggleLock,
		toggleUserLock,
		setOpen,
	};
};
